import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Search, X } from "lucide-react";

export type FilterState = {
  categorias: string[];
  lojas: string[];
  marcas: string[];
  desconto: number;
  avaliacao: number;
  precoMax: number;
};

type Props = {
  state: FilterState;
  set: (s: FilterState) => void;
  categories: string[];
  stores: string[];
  marcas: string[];
};

// ─── Brand registry ────────────────────────────────────────────────────────────
// Mapa: nome normalizado (lowercase) → domínio para Clearbit Logo API
const BRAND_DOMAINS: Record<string, string> = {
  // Lojas / marketplaces
  shein: "shein.com",
  shopee: "shopee.com.br",
  amazon: "amazon.com.br",
  aliexpress: "aliexpress.com",
  "mercado livre": "mercadolivre.com.br",
  mercadolivre: "mercadolivre.com.br",
  // Moda internacional
  zara: "zara.com",
  "h&m": "hm.com",
  hm: "hm.com",
  "forever 21": "forever21.com",
  forever21: "forever21.com",
  "pull&bear": "pullandbear.com",
  pullandbear: "pullandbear.com",
  bershka: "bershka.com",
  stradivarius: "stradivarius.com",
  massimo: "massimodutti.com",
  "massimo dutti": "massimodutti.com",
  mango: "mango.com",
  uniqlo: "uniqlo.com",
  gap: "gap.com",
  "tommy hilfiger": "tommy.com",
  tommy: "tommy.com",
  "calvin klein": "calvinklein.com",
  calvinklein: "calvinklein.com",
  "guess": "guess.com",
  lacoste: "lacoste.com",
  // Moda BR
  renner: "lojasrenner.com.br",
  "lojas renner": "lojasrenner.com.br",
  "c&a": "cea.com.br",
  cea: "cea.com.br",
  riachuelo: "riachuelo.com.br",
  hering: "hering.com.br",
  marisa: "marisa.com.br",
  pernambucanas: "pernambucanas.com.br",
  "farm": "farmrio.com.br",
  "farm rio": "farmrio.com.br",
  animale: "animale.com.br",
  forum: "forum.com.br",
  // Esportes
  nike: "nike.com",
  adidas: "adidas.com.br",
  puma: "puma.com",
  "new balance": "newbalance.com",
  newbalance: "newbalance.com",
  reebok: "reebok.com",
  under: "underarmour.com",
  "under armour": "underarmour.com",
  fila: "fila.com",
  asics: "asics.com",
  mizuno: "mizuno.com.br",
  olympikus: "olympikus.com.br",
  penalty: "penalty.com",
  // Beleza
  loreal: "loreal.com.br",
  "l'oreal": "loreal.com.br",
  "l oreal": "loreal.com.br",
  maybelline: "maybelline.com",
  revlon: "revlon.com",
  nyx: "nyxcosmetics.com",
  mac: "maccosmetics.com",
  natura: "natura.com.br",
  avon: "avon.com.br",
  oboticario: "boticario.com.br",
  "o boticario": "boticario.com.br",
  boticario: "boticario.com.br",
  nivea: "nivea.com.br",
  dove: "dove.com",
  garnier: "garnier.com.br",
  pantene: "pantene.com",
  // Tech / Acessórios
  samsung: "samsung.com",
  apple: "apple.com",
  xiaomi: "xiaomi.com",
  motorola: "motorola.com",
  "jbl": "jbl.com",
  philips: "philips.com.br",
  multilaser: "multilaser.com.br",
  // Calçados
  vans: "vans.com",
  converse: "converse.com",
  "all star": "converse.com",
  havaianas: "havaianas.com.br",
  ipanema: "ipanema.com.br",
  grendene: "grendene.com.br",
  melissa: "melissa.com.br",
  bota: "arezzo.com.br",
  arezzo: "arezzo.com.br",
  schutz: "schutz.com.br",
  // Joias / Acessórios
  pandora: "pandora.net",
  vivara: "vivara.com.br",
  rommanel: "rommanel.com.br",
};

function getBrandDomain(name: string): string | null {
  return BRAND_DOMAINS[name.toLowerCase().trim()] ?? null;
}

// Cor determinística baseada no nome da marca
const PALETTE = [
  "bg-pink-100 text-pink-700",
  "bg-purple-100 text-purple-700",
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-orange-100 text-orange-700",
  "bg-rose-100 text-rose-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
  "bg-amber-100 text-amber-700",
  "bg-cyan-100 text-cyan-700",
];

function brandPaletteClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

function brandInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ─── BrandAvatar ───────────────────────────────────────────────────────────────
function BrandAvatar({ name }: { name: string }) {
  const [error, setError] = useState(false);
  const domain = getBrandDomain(name);

  if (domain && !error) {
    return (
      <img
        src={`https://logo.clearbit.com/${domain}?size=48`}
        alt=""
        width={24}
        height={24}
        // A sidebar fica escondida no mobile: sem lazy, o navegador baixava as
        // ~150 logos de terceiros mesmo sem nada disso aparecer na tela.
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        className="h-6 w-6 shrink-0 rounded-full object-contain ring-1 ring-border/40 bg-white p-[2px]"
        onError={() => setError(true)}
      />
    );
  }

  // Fallback: badge colorido com iniciais
  return (
    <span
      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[9px] font-bold leading-none ${brandPaletteClass(name)}`}
    >
      {brandInitials(name)}
    </span>
  );
}

// ─── Filters ──────────────────────────────────────────────────────────────────
export function Filters({ state, set, categories, stores, marcas }: Props) {
  const [marcaSearch, setMarcaSearch] = useState("");

  const toggle = (key: "categorias" | "lojas" | "marcas", v: string) => {
    const arr = state[key];
    set({ ...state, [key]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
  };

  const filteredMarcas = marcaSearch.trim()
    ? marcas.filter((m) => m.toLowerCase().includes(marcaSearch.toLowerCase()))
    : marcas;

  return (
    <div
      role="region"
      aria-label="Filtros de busca"
      className="sticky top-44 hidden h-fit w-64 shrink-0 rounded-2xl border border-border/70 bg-card p-5 shadow-[var(--shadow-card)] lg:block"
    >
      <h3 className="mb-4 font-display text-lg">Filtros</h3>

      <Section title="Categoria">
        {categories.map((c) => (
          <CheckRow key={c} checked={state.categorias.includes(c)} onChange={() => toggle("categorias", c)} label={c} />
        ))}
      </Section>

      {marcas.length > 0 && (
        <Section title="Marca">
          {/* Barra de pesquisa */}
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={marcaSearch}
              onChange={(e) => setMarcaSearch(e.target.value)}
              placeholder="Pesquisar marca..."
              aria-label="Pesquisar marca"
              className="w-full rounded-lg border border-border/60 bg-muted/40 py-1.5 pl-8 pr-7 text-xs text-foreground placeholder:text-muted-foreground/70 focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            {marcaSearch && (
              <button
                type="button"
                onClick={() => setMarcaSearch("")}
                aria-label="Limpar busca de marca"
                className="absolute right-0 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          {/* Lista com scroll */}
          <div className="max-h-52 overflow-y-auto overscroll-contain pr-0.5 scrollbar-thin">
            {filteredMarcas.length > 0 ? (
              filteredMarcas.map((m) => (
                <BrandRow
                  key={m}
                  name={m}
                  checked={state.marcas.includes(m)}
                  onChange={() => toggle("marcas", m)}
                />
              ))
            ) : (
              <p className="py-2 text-center text-xs text-muted-foreground">Nenhuma marca encontrada</p>
            )}
          </div>
          {/* Contador de selecionadas */}
          {state.marcas.length > 0 && (
            <button
              onClick={() => set({ ...state, marcas: [] })}
              className="mt-2 text-[11px] text-primary hover:underline"
            >
              Limpar {state.marcas.length} selecionada{state.marcas.length > 1 ? "s" : ""}
            </button>
          )}
        </Section>
      )}

      <Section title={`Preço até R$ ${state.precoMax}`}>
        <Slider
          value={[state.precoMax]}
          min={20}
          max={300}
          step={10}
          onValueChange={(v) => set({ ...state, precoMax: v[0] })}
          aria-label="Preço máximo em reais"
          className="my-2"
        />
        <div className="flex justify-between text-[11px] text-muted-foreground">
          <span>R$ 20</span><span>R$ 300+</span>
        </div>
      </Section>

      <Section title="Desconto mínimo">
        {[10, 20, 30, 50].map((d) => (
          <button
            key={d}
            onClick={() => set({ ...state, desconto: state.desconto === d ? 0 : d })}
            className={`mb-1.5 mr-1.5 inline-flex items-center rounded-full border px-2.5 py-1 text-xs transition ${
              state.desconto === d
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-muted/50 text-foreground/75 hover:border-primary/40"
            }`}
          >
            A partir de {d}%
          </button>
        ))}
      </Section>

      <Section title="Loja parceira">
        {stores.map((l) => (
          <CheckRow key={l} checked={state.lojas.includes(l)} onChange={() => toggle("lojas", l)} label={l} />
        ))}
      </Section>

      <Section title="Avaliação mínima">
        {[4, 4.5].map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => set({ ...state, avaliacao: state.avaliacao === a ? 0 : a })}
            aria-pressed={state.avaliacao === a}
            aria-label={`Avaliação ${a} ou mais`}
            className={`mb-1.5 mr-1.5 inline-flex min-h-[1.75rem] items-center rounded-full border px-3 py-1.5 text-xs transition ${
              state.avaliacao === a
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-muted/50 text-foreground/75 hover:border-primary/40"
            }`}
          >
            ★ {a}+
          </button>
        ))}
      </Section>
    </div>
  );
}

// ─── Primitives ────────────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border/60 py-4 last:border-b-0">
      <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <div>{children}</div>
    </div>
  );
}

function CheckRow({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <label className="flex min-h-[2rem] cursor-pointer items-center gap-2 py-1.5 text-sm text-foreground/85 hover:text-foreground">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        aria-label={label}
        className="data-[state=checked]:border-primary data-[state=checked]:bg-primary"
      />
      <span>{label}</span>
    </label>
  );
}

function BrandRow({ name, checked, onChange }: { name: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex min-h-[2rem] cursor-pointer items-center gap-2 py-1.5 text-sm text-foreground/85 hover:text-foreground">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        aria-label={name}
        className="data-[state=checked]:border-primary data-[state=checked]:bg-primary"
      />
      <BrandAvatar name={name} />
      <span className="truncate">{name}</span>
    </label>
  );
}
